import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, ChevronRightIcon, InfoSignIcon, Pane, Paragraph, SideSheet, toaster } from "evergreen-ui";
import {
    AbstractTableViewProvider
} from "../../common/tables/view/AbstractTableViewProvider";
import { useTableViewContext } from "../../common/tables/view/AbstractTableViewProvider";
import { TableView } from "../../common/tables/view/TableView";
import { CostMetadataTableRow } from "../../../../common/tables/cost-metadata/CostMetadataTableRow";
import { CostMetadata, CostPayoutModifier, ShopifyProductVariant } from "../../../../common/models";
import { CostMetadataCreateInput, CostMetadataUpdateInput } from "../../../../common/inputs/CostMetadataInput";
import { QueryInput } from "../../../../common/query-filters";
import {
    queryCostMetadataByShopifyProductId,
    useCreateCostMetadataMutation,
    useUpdateCostMetadataMutation,
} from "../../datastore/CostMetadataDataStore";
import { queryAllVariantsByProductListingId, queryVariantsWithCostMetadata } from "../../datastore/ShopifyProductDataStore";
import { queryAllCostPayoutModifiers } from "../../datastore/CostPayoutModifierDataStore";
import { DefaultCostMetadataTableConfig } from "./cost-metadata/tables/config/DefaultCostMetadataTableConfig";
import { CostMetadataTableCellFactory } from "./cost-metadata/factory/CostMetadataTableCellFactory";
import { VariantAssignmentTableCellFactory } from "./cost-metadata/factory/VariantAssignmentTableCellFactory";
import { DefaultCostMetadataVariantSideSheetConfig } from "./cost-metadata/tables/config/DefaultCostMetadataVariantSideSheetConfig";
import { VariantTableRow } from "../../../../common/tables/product-listing/variants/VariantTableRow";
import { TableCellFactory } from "../../common/tables/factory";
import { EmptyState } from "../../common/EmptyState";
import { RampartAuthorized } from "../../rampart/RampartAuthorized";
import {
    CostMetadataTableRowSideSheetForm,
} from "./cost-metadata/tables/CostMetadataTableRowSideSheetForm";

export type CostMetadataWithRelations = CostMetadata & {
    shopifyProductVariant?: ShopifyProductVariant[];
    costPayoutModifier?: CostPayoutModifier | null;
};

type VariantWithAssignment = ShopifyProductVariant & { _assigned: boolean; _lastAssigned: boolean };

const VariantsViewSideSheet: FC<{
    isShown: boolean;
    variants: ShopifyProductVariant[];
    productListingId: string;
    costMetadataId: string;
    modifier?: string;
    startDate?: string;
    unitCost?: number;
    cpmName?: string;
    onDismiss: () => void;
    onSave: (costMetadataId: string, variantIds: string[]) => void;
}> = ({ isShown, variants, productListingId, costMetadataId, modifier, startDate, unitCost, cpmName, onDismiss, onSave }) => {
    const assignedIds = useMemo(() => new Set(variants.map(v => String(v.id))), [variants]);
    const [dirty, setDirty] = useState(false);
    const latestVariantIds = useRef<string[] | null>(null);

    const [updateCostMetadataMutation, { error: variantMutationError }] = useUpdateCostMetadataMutation();

    useEffect(() => {
        if (variantMutationError) {
            toaster.danger("Failed to update variant assignment. Please try again.");
        }
    }, [variantMutationError]);

    useEffect(() => {
        if (!isShown) {
            setDirty(false);
            latestVariantIds.current = null;
        }
    }, [isShown]);

    const variantCellFactory = useMemo(() => new VariantAssignmentTableCellFactory(), []);

    const queryTableData = useCallback(async (_input: QueryInput<VariantTableRow>): Promise<VariantWithAssignment[]> => {
        const allVariants = await queryVariantsWithCostMetadata(productListingId);
        const assignedCount = allVariants.filter(v => assignedIds.has(String(v.id))).length;
        return allVariants.map(v => ({
            ...v,
            _assigned: assignedIds.has(String(v.id)),
            _lastAssigned: assignedIds.has(String(v.id)) && assignedCount <= 1,
        }));
    }, [productListingId, assignedIds]);

    const onUpdateRow = useCallback((
        variantList: VariantWithAssignment[],
        id: string | number,
        columnName: keyof VariantTableRow,
        value: string | number | boolean | null,
    ): VariantWithAssignment[] => {
        if (columnName !== "costMetadataCount") return variantList;

        const isAssigning = Boolean(value);

        // Prevent unassigning if this is the last assigned variant
        if (!isAssigning) {
            const currentAssignedCount = variantList.filter(v => v._assigned).length;
            if (currentAssignedCount <= 1) {
                return variantList;
            }
        }

        const toggled = variantList.map(v =>
            String(v.id) === String(id) ? { ...v, _assigned: isAssigning } : v
        );

        const newAssignedCount = toggled.filter(v => v._assigned).length;
        const updated = toggled.map(v => ({
            ...v,
            _lastAssigned: v._assigned && newAssignedCount <= 1,
        }));

        const newVariantIds = updated.filter(v => v._assigned).map(v => String(v.id));
        latestVariantIds.current = newVariantIds;
        // TODO: Add optimistic rollback on mutation failure (see #1297)
        updateCostMetadataMutation({ id: costMetadataId, variantIds: newVariantIds } as CostMetadataUpdateInput);
        setDirty(true);

        return updated;
    }, [costMetadataId, updateCostMetadataMutation]);

    const handleClose = useCallback(() => {
        if (dirty && latestVariantIds.current) {
            onSave(costMetadataId, latestVariantIds.current);
        }
        onDismiss();
    }, [dirty, costMetadataId, onSave, onDismiss]);

    const emptyViewProps = useMemo(() => ({
        primaryText: "No variants available",
        secondaryText: "",
    }), []);

    const errorViewProps = useMemo(() => ({
        primaryText: "Failed to load variants",
        secondaryText: "",
    }), []);

    return (
        <SideSheet
            width="65vw"
            isShown={isShown}
            onCloseComplete={handleClose}
        >
            <Pane paddingBottom={8}>
                <h3 className="multi-tab-heading" style={{ marginBottom: 0 }}>
                    <span className="multi-tab-breadcrumb-parent">Cost Metadata</span>
                    <ChevronRightIcon color="muted" size={16} marginX={4} style={{ verticalAlign: 'middle' }} />
                    Product Variants
                </h3>
                <Pane display="flex" flexWrap="wrap" alignItems="center" gap={8} paddingLeft={36} marginTop={16} marginBottom={24}>
                    {modifier ? (
                        <Badge color="neutral" textTransform="none" fontWeight="normal" paddingX={10} borderRadius={10}>
                            <strong>Cost Metadata Name</strong>&ensp;{modifier}
                        </Badge>
                    ) : (
                        <Badge color="neutral" textTransform="none" fontWeight="normal" paddingX={10} borderRadius={10}>
                            <strong>Cost Metadata ID</strong>&ensp;{costMetadataId}
                        </Badge>
                    )}
                    {startDate && (
                        <Badge color="neutral" textTransform="none" fontWeight="normal" paddingX={10} borderRadius={10}>
                            <strong>Cost Metadata Start Date</strong>&ensp;{new Date(startDate).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}
                        </Badge>
                    )}
                </Pane>
                <Paragraph size={300} color="muted" paddingLeft={36} marginTop={12} marginBottom={0}>
                    Toggle the checkbox in any one of the <strong><code>ProductVariant</code></strong> rows below to assign it to this selected <strong><code>CostMetadata</code></strong>.
                </Paragraph>
            </Pane>
            <AbstractTableViewProvider
                queryTableData={queryTableData}
                onUpdateRow={onUpdateRow}
                updateRowLoading={false}
                updateRowCalled={false}
                updateRowError={false}
                tableCellFactory={variantCellFactory}
                savedTableConfigProps={{
                    tableName: "costMetadataVariantSideSheet",
                    identifiers: ["v1"],
                    defaultConfigValue: DefaultCostMetadataVariantSideSheetConfig,
                }}
            >
                <TableView
                    emptyViewProps={emptyViewProps}
                    errorViewProps={errorViewProps}
                />
            </AbstractTableViewProvider>
        </SideSheet>
    );
};

/**
 * Bridge component rendered inside AbstractTableViewProvider to give
 * the view side sheets access to the provider's updateRow function.
 */
const CostMetadataSideSheetBridge: FC<{
    variantsSideSheet: { isShown: boolean; variants: ShopifyProductVariant[]; costMetadataId: string; modifier?: string; startDate?: string; unitCost?: number; cpmName?: string };
    productListingId: string;
    onDismissVariants: () => void;
    onSaveVariants: (costMetadataId: string, variantIds: string[], updateRow: (id: string | number, columnName: keyof CostMetadataTableRow, value: string | number | boolean | null, label?: string) => void) => void;
}> = ({ variantsSideSheet, productListingId, onDismissVariants, onSaveVariants }) => {
    const { updateRow } = useTableViewContext<CostMetadataTableRow, CostMetadata>();

    return (
        <VariantsViewSideSheet
            isShown={variantsSideSheet.isShown}
            variants={variantsSideSheet.variants}
            productListingId={productListingId}
            costMetadataId={variantsSideSheet.costMetadataId}
            modifier={variantsSideSheet.modifier}
            startDate={variantsSideSheet.startDate}
            unitCost={variantsSideSheet.unitCost}
            cpmName={variantsSideSheet.cpmName}
            onDismiss={onDismissVariants}
            onSave={(costMetadataId, variantIds) => onSaveVariants(costMetadataId, variantIds, updateRow)}
        />
    );
};

export const CostMetadataTab: FC<{ shopifyProductId?: string | null; productListingId: string; onDataLoaded?: (hasData: boolean) => void }> = ({ shopifyProductId, productListingId, onDataLoaded }) => {
    if (!shopifyProductId) {
        return (
            <Pane padding={24}>
                <EmptyState
                    title="No Shopify product linked"
                    subtitle="This product listing is not linked to a Shopify product"
                >
                    <InfoSignIcon size={60} />
                </EmptyState>
            </Pane>
        );
    }

    return (
        <RampartAuthorized requiredGroups={["juniper.self-serve.admin"]}>
            <CostMetadataTableContent shopifyProductId={shopifyProductId} productListingId={productListingId} onDataLoaded={onDataLoaded} />
        </RampartAuthorized>
    );
};

const CostMetadataTableContent: FC<{ shopifyProductId: string; productListingId: string; onDataLoaded?: (hasData: boolean) => void }> = ({ shopifyProductId, productListingId, onDataLoaded }) => {
    const defaultCreateInput: Partial<CostMetadataCreateInput> = useMemo(() => {
        return {
            variantIds: [],
        };
    }, []);

    const [showCreateRowForm, setShowCreateRowForm] = useState<boolean>(false);
    const [createInput, setCreateInput] = useState<Partial<CostMetadataCreateInput>>(defaultCreateInput);
    const [availableVariants, setAvailableVariants] = useState<ShopifyProductVariant[]>([]);
    const [costPayoutModifiers, setCostPayoutModifiers] = useState<CostPayoutModifier[]>([]);

    // Load all variants on mount (used by filter dropdown and create side sheet)
    useEffect(() => {
        queryAllVariantsByProductListingId(productListingId)
            .then(setAvailableVariants)
            .catch(() => setAvailableVariants([]));
    }, [productListingId]);

    // Variants cell-click side sheet state
    const [variantsSideSheet, setVariantsSideSheet] = useState<{ isShown: boolean; variants: ShopifyProductVariant[]; costMetadataId: string; modifier?: string; startDate?: string; unitCost?: number; cpmName?: string }>({
        isShown: false,
        variants: [],
        costMetadataId: "",
    });

    // Load CPMs eagerly for inline dropdown
    useEffect(() => {
        queryAllCostPayoutModifiers()
            .then(setCostPayoutModifiers)
            .catch(() => setCostPayoutModifiers([]));
    }, []);

    const onVariantsCellClick = useCallback((rowData: CostMetadata) => {
        const data = rowData as CostMetadataWithRelations;
        const variants = data.shopifyProductVariant ?? [];
        setVariantsSideSheet({
            isShown: true,
            variants,
            costMetadataId: rowData.id,
            modifier: rowData.modifier || undefined,
            startDate: rowData.startDate ? String(rowData.startDate) : undefined,
            unitCost: rowData.unitCost,
            cpmName: data.costPayoutModifier?.name,
        });
    }, []);

    const costMetadataTableCellFactory: TableCellFactory<CostMetadataTableRow, CostMetadata> = useMemo(() => {
        return new CostMetadataTableCellFactory({
            onVariantsCellClick,
            variantFilterOptions: availableVariants,
            costPayoutModifiers,
        });
    }, [onVariantsCellClick, availableVariants, costPayoutModifiers]);

    const queryTableData = useCallback((input: QueryInput<CostMetadataTableRow>) => {
        const variantSkusExpr = input.expressions.find(e => e.columnName === 'variantSkus');
        const startDateExpr = input.expressions.find(e => e.columnName === 'startDate');

        return queryCostMetadataByShopifyProductId({
            shopifyProductId,
            variantId: variantSkusExpr?.columnValue?.[0] as string | undefined,
            startDateFrom: startDateExpr?.columnValue?.[0] as string | undefined,
            startDateTo: startDateExpr?.columnValue?.[1] as string | undefined,
            options: {
                take: input.pageSize,
            },
        });
    }, [shopifyProductId]);

    const [
        createCostMetadata,
        {
            loading: createLoading,
            error: createError,
            called: createCalled,
        }
    ] = useCreateCostMetadataMutation();

    const [
        updateCostMetadata,
        {
            loading,
            called,
            error,
        }
    ] = useUpdateCostMetadataMutation();

    const onOpenSideSheet = useCallback(() => {
        setShowCreateRowForm(true);
    }, []);

    const onSubmitSideSheet = useCallback(() => {
        createCostMetadata(createInput as CostMetadataCreateInput);
    }, [createCostMetadata, createInput]);

    const onCancelSideSheet = useCallback(() => {
        setShowCreateRowForm(false);
    }, []);

    const NUMERIC_COLUMNS: Set<string> = useMemo(() => new Set([
        "unitCost", "juniperServiceFee", "fulfillmentFee", "processingFeePercent", "shippingSubsidy",
    ]), []);

    const onUpdateRow = useCallback((
        costMetadataList: CostMetadata[],
        id: string | number,
        columnName: keyof CostMetadataTableRow,
        value: string | number | boolean | null,
        label?: string,
    ): CostMetadata[] => {
        return costMetadataList.map((costMetadata: CostMetadata) => {
            if (costMetadata.id !== id) {
                return costMetadata;
            }

            const data = costMetadata as CostMetadataWithRelations;

            // Inline dropdown: CPM change
            if (columnName === "costPayoutModifierId" as keyof CostMetadataTableRow) {
                const costPayoutModifierId = (value as string) || null;
                updateCostMetadata({ id: costMetadata.id, costPayoutModifierId });
                const fullCpm = costPayoutModifiers.find(c => c.id === costPayoutModifierId) ?? null;
                return {
                    ...data,
                    costPayoutModifierId: costPayoutModifierId,
                    costPayoutModifier: fullCpm,
                } as CostMetadata;
            }

            // Side sheet save: variant reassignment (routed through updateRow)
            if (columnName === "variantSkus" as keyof CostMetadataTableRow) {
                const variantIds = value ? String(value).split(",") : [];
                const variantSkus = label ? label.split(",") : [];
                updateCostMetadata({ id: costMetadata.id, variantIds });
                return {
                    ...data,
                    shopifyProductVariant: variantIds.map((vid, i) => ({
                        id: vid,
                        sku: variantSkus[i] ?? vid,
                    } as unknown as ShopifyProductVariant)),
                } as CostMetadata;
            }

            // Regular inline edit
            let truncatedValue = value;
            if (NUMERIC_COLUMNS.has(columnName as string) && typeof value === "number") {
                truncatedValue = Math.round(value * 100) / 100;
            }

            const costMetadataToUpdate = {
                ...costMetadata,
                [columnName]: truncatedValue,
            };

            updateCostMetadata(costMetadataToUpdate);

            return costMetadataToUpdate;
        });
    }, [updateCostMetadata, costPayoutModifiers]);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No cost metadata found",
            secondaryText: "Add Cost Metadata",
            onClickSecondaryText: () => setShowCreateRowForm(true),
        };
    }, []);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load cost metadata",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    // Reset form state after successful creation
    useEffect(() => {
        if (!createCalled) {
            return;
        }

        if (!createLoading && !createError) {
            setCreateInput(defaultCreateInput);
            setShowCreateRowForm(false);
        }
    }, [createLoading, createCalled, createError, defaultCreateInput]);

    const onSaveVariants = useCallback((costMetadataId: string, variantIds: string[], updateRow: (id: string | number, columnName: keyof CostMetadataTableRow, value: string | number | boolean | null, label?: string) => void) => {
        const skus = availableVariants.filter(v => variantIds.includes(String(v.id))).map(v => v.sku || String(v.id));
        updateRow(costMetadataId, "variantSkus" as keyof CostMetadataTableRow, variantIds.join(","), skus.join(","));
        setVariantsSideSheet({ isShown: false, variants: [], costMetadataId: "" });
    }, [availableVariants]);

    return (
        <>
            <AbstractTableViewProvider
                queryTableData={queryTableData}
                onUpdateRow={onUpdateRow}
                updateRowLoading={loading}
                updateRowCalled={called}
                updateRowError={error}
                createRowCalled={createCalled}
                createRowError={createError}
                createRowLoading={createLoading}
                tableCellFactory={costMetadataTableCellFactory}
                savedTableConfigProps={{
                    tableName: "costMetadata",
                    identifiers: ["v2"],
                    defaultConfigValue: DefaultCostMetadataTableConfig,
                }}
            >
                <CostMetadataDataNotifier onDataLoaded={onDataLoaded} />
                <CostMetadataWarningBanner onAddClick={onOpenSideSheet} />
                <TableView
                    errorViewProps={errorViewProps}
                    emptyViewProps={emptyViewProps}
                />
                <CostMetadataSideSheetBridge
                    variantsSideSheet={variantsSideSheet}
                    productListingId={productListingId}
                    onDismissVariants={() => setVariantsSideSheet({ isShown: false, variants: [], costMetadataId: "" })}
                    onSaveVariants={onSaveVariants}
                />
            </AbstractTableViewProvider>
            <CostMetadataTableRowSideSheetForm
                isShown={showCreateRowForm}
                costMetadata={createInput}
                variants={availableVariants}
                costPayoutModifiers={costPayoutModifiers}
                mutationLoading={createLoading}
                mutationCalled={createCalled}
                mutationError={createError}
                onDismiss={onCancelSideSheet}
                onOpen={onOpenSideSheet}
                onSubmit={onSubmitSideSheet}
                onInputChanged={setCreateInput}
            />
        </>
    );
};

const CostMetadataDataNotifier: FC<{ onDataLoaded?: (hasData: boolean) => void }> = ({ onDataLoaded }) => {
    const { data, dataQuerying } = useTableViewContext();
    const lastReported = useRef<boolean | null>(null);

    useEffect(() => {
        if (dataQuerying || !onDataLoaded) {
            return;
        }
        const hasData = data.length > 0;
        if (lastReported.current !== hasData) {
            lastReported.current = hasData;
            onDataLoaded(hasData);
        }
    }, [data, dataQuerying, onDataLoaded]);

    return null;
};

const CostMetadataWarningBanner: FC<{ onAddClick: () => void }> = ({ onAddClick }) => {
    const { data, dataQuerying } = useTableViewContext();

    if (dataQuerying || data.length > 0) {
        return null;
    }

    return (
        <Alert
            intent="danger"
            title="This product listing has no Cost Metadata. Payout calculations require Cost Metadata to determine cost structures for each variant."
            marginBottom={16}
            marginX={16}
            marginTop={16}
        >
            <Button appearance="primary" marginTop={8} onClick={onAddClick}>
                Add Cost Metadata
            </Button>
        </Alert>
    );
};
